<template>
  <!-- Selector de entidad: ocupa la rejilla (carril + lienzo) mientras la definición no tiene entidad o al
       «Cambiar…». Cada tarjeta es una raíz con sus extensiones 1:1; las que ya usa otra definición, inactivas. -->
  <section class="ep" :aria-labelledby="titleId" @keydown.esc="onEsc">
    <h2 :id="titleId" class="ep-title">{{ t('ep.title') }}</h2>
    <p class="ep-hint">{{ t('ep.hint') }}</p>
    <div class="ep-tools">
      <input v-if="roots.length > FILTER_FROM" ref="filterRef" v-model="filter" class="in ep-filter"
             :placeholder="t('pal.filter')" :aria-label="t('pal.filter')" />
      <span class="grow" />
      <button v-if="cancelable" type="button" class="btn" @click="emit('cancel')">{{ t('ep.cancel') }}</button>
    </div>
    <div ref="gridRef" class="ep-grid">
      <button v-for="e in shown" :key="e.className" type="button" class="ep-card"
              :class="{ cur: e.className === current }" :disabled="e.off" :title="e.classes" @click="emit('pick', e.className)">
        <span class="ep-name"><span class="mc-ico entity" aria-hidden="true">E</span><b class="mono">{{ e.name }}</b>
          <span v-if="e.className === current" class="ep-tag">{{ t('ep.current') }}</span></span>
        <span class="ep-tables mono">{{ e.tables }}</span>
        <span class="ep-meta">{{ t('ep.fields', { n: e.fields }) }} · {{ t('ep.key', { origin: e.origin, id: e.id }) }}</span>
        <span v-if="e.takenBy" class="ep-taken">{{ t('ins.rootTaken', { name: e.takenBy }) }}</span>
      </button>
    </div>
    <p v-if="allTaken" class="hint warn">{{ t('ep.allTaken') }}</p>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';
import { catalog, comboName, comboTables, entityByClass, extensionsOf } from '@vem/catalog.js';

let seq = 0;
const FILTER_FROM = 6;

const props = defineProps({
  /** Raíz actual de la definición ('' si no tiene). */
  current: { type: String, default: '' },
  /** Id de la definición en edición: su propia raíz no cuenta como «ya usada». */
  defId: { type: String, default: null },
  /** Con «Cancelar» (al cambiar de entidad). */
  cancelable: { type: Boolean, default: false },
});
const emit = defineEmits(['pick', 'cancel']);

const titleId = `ep-title-${++seq}`;
const filter = ref('');
const filterRef = ref(null);
const gridRef = ref(null);

const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const roots = computed(() => catalog.entities.filter((e) => e.role === 'root').map((e) => {
  const classes = [e.className, ...extensionsOf(e.className)];
  const takenBy = e.usedBy && e.usedBy.id !== props.defId ? e.usedBy.name : null;
  return {
    className: e.className,
    classes: classes.join(' + '),
    name: comboName(e.className),
    tables: comboTables(e.className),
    fields: classes.reduce((n, c) => n + (entityByClass(c)?.fields.length || 0), 0),
    origin: e.originField,
    id: e.sourceIdField,
    takenBy,
    off: !!takenBy || e.className === props.current,
  };
}));

const shown = computed(() => {
  const q = fold(filter.value.trim());
  return q ? roots.value.filter((e) => fold(`${e.name} ${e.tables} ${e.classes}`).includes(q)) : roots.value;
});

const allTaken = computed(() => roots.value.length > 0 && roots.value.every((e) => e.takenBy));

function onEsc(e) {
  if (!props.cancelable) return;
  e.stopPropagation();
  emit('cancel');
}

/** Buscador si lo hay; si no, la primera tarjeta disponible. */
function focus() {
  if (filterRef.value) filterRef.value.focus();
  else gridRef.value?.querySelector('.ep-card:not(:disabled)')?.focus();
}

onMounted(() => nextTick(focus));

defineExpose({ focus });
</script>

<style>
.vem-root .ep {
  flex: 1 1 auto; min-width: 0; overflow-y: auto; background: var(--panel); border: 1px solid var(--line2);
  border-radius: 14px; padding: 22px 24px;
}
.vem-root .ep-title { margin: 0; font-size: 19px; font-weight: 600; letter-spacing: -.01em; }
.vem-root .ep-hint { margin: 4px 0 14px; color: var(--tx2); max-width: 720px; }
.vem-root .ep-tools { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.vem-root .ep-filter { width: min(320px, 100%); }
.vem-root .ep-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
.vem-root .ep-card {
  display: flex; flex-direction: column; align-items: flex-start; gap: 3px; text-align: left; cursor: pointer;
  background: var(--panel); color: var(--tx); border: 1px solid var(--line2); border-left: 3px solid var(--lime);
  border-radius: 11px; padding: 12px 14px; transition: box-shadow .15s, border-color .15s;
}
.vem-root .ep-card:hover:not(:disabled) { border-color: var(--lime); box-shadow: var(--lift, 0 8px 24px -12px rgba(31, 35, 40, .25)); }
.vem-root .ep-card:disabled { cursor: default; opacity: .55; border-left-color: var(--port); }
.vem-root .ep-card.cur { opacity: 1; background: var(--lime-bg); border-left-color: var(--lime); }
.vem-root .ep-name { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vem-root .ep-name b { font-size: 14px; color: var(--lime); overflow-wrap: anywhere; }
.vem-root .ep-tag { font-family: var(--mono); font-size: 9.5px; padding: 1px 6px; border-radius: 4px; background: var(--lime-bg); color: var(--lime); }
.vem-root .ep-tables { font-size: 11px; color: var(--tx3); padding-left: 32px; }
.vem-root .ep-meta { font-size: 12px; color: var(--tx2); padding-left: 32px; }
.vem-root .ep-taken { font-size: 12px; color: var(--amber); padding-left: 32px; }
</style>
